/* ============================================================
   FF33 FITTER
   1. Landing  — pick a hand
   2. Flight   — pick the typical ball flight; a tracer flies tee → apex (the dot) → fairway
   3. Results  — the recommended hosel setting mirrors the miss through A1: (x, y) → (-x, -y)

   GSAP (core only) drives the animations. Without it, or with reduced motion on,
   every animation jumps straight to its end state.
============================================================ */

/**
 * @typedef {object} FitNode
 * @property {number} x Column on the 15×15 grid (-7…7), + is right
 * @property {number} y Row on the 15×15 grid (-7…7), + is high
 * @property {string} id Hosel setting
 * @property {'primary'|'secondary'|'tertiary'} tier
 * @property {string[]} recs "Also try" settings
 * @property {string[]} dupes Settings with the same loft/lie
 */

/** @type {FitNode[]} */
const NODES = [
  { x: 0, y: 0, id: 'a1', tier: 'primary', recs: [], dupes: ['b2', 'c3', 'd4', 'h8', 'g7', 'f6', 'e5'] },
  { x: 0, y: 6, id: 'c7', tier: 'secondary', recs: ['d6', 'c8', 'b7'], dupes: [] },
  { x: 4, y: 4, id: 'b6', tier: 'secondary', recs: ['a7', 'b7', 'a6'], dupes: [] },
  { x: 6, y: 0, id: 'a5', tier: 'secondary', recs: ['b4', 'a6', 'a4'], dupes: [] },
  { x: 4, y: -4, id: 'h4', tier: 'secondary', recs: ['g4', 'a3', 'a4'], dupes: [] },
  { x: 0, y: -6, id: 'g3', tier: 'secondary', recs: ['f3', 'f4', 'g4'], dupes: [] },
  { x: -4, y: -4, id: 'f2', tier: 'secondary', recs: ['f1', 'g1', 'f3'], dupes: [] },
  { x: -6, y: 0, id: 'e1', tier: 'secondary', recs: ['d1', 'd2', 'f1'], dupes: [] },
  { x: -4, y: 4, id: 'd8', tier: 'secondary', recs: ['d1', 'c1', 'c8'], dupes: [] },
  { x: 1, y: 2, id: 'a8', tier: 'tertiary', recs: ['d6', 'a7'], dupes: ['d5'] },
  { x: 2, y: 1, id: 'c4', tier: 'tertiary', recs: ['a7', 'b4'], dupes: ['h7'] },
  { x: 2, y: -1, id: 'b3', tier: 'tertiary', recs: ['b4', 'a3'], dupes: ['g6'] },
  { x: 1, y: -2, id: 'a2', tier: 'tertiary', recs: ['f4', 'a3'], dupes: ['f5'] },
  { x: -1, y: -2, id: 'h1', tier: 'tertiary', recs: ['g1', 'f4'], dupes: ['e4'] },
  { x: -2, y: -1, id: 'd3', tier: 'tertiary', recs: ['d2', 'g1'], dupes: ['g8'] },
  { x: -2, y: 1, id: 'c2', tier: 'tertiary', recs: ['d2', 'c1'], dupes: ['f7'] },
  { x: -1, y: 2, id: 'b1', tier: 'tertiary', recs: ['c1', 'd6'], dupes: ['e6'] },
  { x: 0, y: 4, id: 'd6', tier: 'tertiary', recs: ['c7', 'c8', 'b7'], dupes: ['b8'] },
  { x: 3, y: 3, id: 'a7', tier: 'tertiary', recs: ['b6', 'b7', 'a6'], dupes: ['c5'] },
  { x: 4, y: 0, id: 'b4', tier: 'tertiary', recs: ['a5', 'a6', 'a4'], dupes: ['h6'] },
  { x: 3, y: -3, id: 'a3', tier: 'tertiary', recs: ['h4', 'g4', 'a4'], dupes: ['g5'] },
  { x: 0, y: -4, id: 'f4', tier: 'tertiary', recs: ['g3', 'f3', 'g4'], dupes: ['h2'] },
  { x: -3, y: -3, id: 'g1', tier: 'tertiary', recs: ['f2', 'f1', 'f3'], dupes: ['e3'] },
  { x: -4, y: 0, id: 'd2', tier: 'tertiary', recs: ['e1', 'd1', 'f1'], dupes: ['f8'] },
  { x: -3, y: 3, id: 'c1', tier: 'tertiary', recs: ['d8', 'd1', 'c8'], dupes: ['e7'] },
  { x: 2, y: 5, id: 'b7', tier: 'tertiary', recs: ['c7', 'b6'], dupes: ['c6'] },
  { x: 5, y: 2, id: 'a6', tier: 'tertiary', recs: ['b6', 'a5'], dupes: ['b5'] },
  { x: 5, y: -2, id: 'a4', tier: 'tertiary', recs: ['a5', 'h4'], dupes: ['h5'] },
  { x: 2, y: -5, id: 'g4', tier: 'tertiary', recs: ['g3', 'h4'], dupes: ['h3'] },
  { x: -2, y: -5, id: 'f3', tier: 'tertiary', recs: ['f2', 'g3'], dupes: ['g2'] },
  { x: -5, y: -2, id: 'f1', tier: 'tertiary', recs: ['e1', 'f2'], dupes: ['e2'] },
  { x: -5, y: 2, id: 'd1', tier: 'tertiary', recs: ['e1', 'd8'], dupes: ['d8'] },
  { x: -2, y: 5, id: 'c8', tier: 'tertiary', recs: ['d8', 'c7'], dupes: ['d7'] },
];

/** Loft (by row) and lie (by column) printed on the FF grid edges, index = 7 - row / col + 7. */
const FF_SCALE = ['', '+2', '+1.7', '+1.4', '+1', '+0.7', '+0.3', 'STD', '-0.3', '-0.7', '-1', '-1.4', '-1.7', '-2', ''];

/* ============================================================
   FLIGHT GEOMETRY
   Everything lives in one 600×900 viewBox that the fairway photo is
   calibrated to: tee at the bottom centre, horizon at y=594 (66% of the
   photo), vanishing point at x=300. The dots sit on the flower-circle
   intersections, OFFSET units from the centre of the top 600×600 square.
============================================================ */
const CX = 300;
const CY = 300;
const TEE_Y = 900;
const HORIZON_Y = 594;
const OFFSET = [0, 35, 85, 120, 170, 205, 240];

const CAMERA_Z = 3; // distance from camera to tee, in the same units as CARRY_Z
const CARRY_Z = 55; // lands just past the tree line, like the original artwork
const GROUND_K = (TEE_Y - HORIZON_Y) * CAMERA_Z;
const APEX_T = 0.58; // share of flight time spent climbing
const DRIFT = 0.75; // outward drift on the way down, as a share of the apex offset…
const DRIFT_MAX = 80; // …easing off toward this cap so the widest shots stay in perspective
const DRAW_BIAS = -14; // keeps straight shots from retracing their own climb
const DRAW_FADE = 85; // the bias fades out by the first ring so side shots never hook back
const TRAIL_WIDTH = [3, 0.8]; // tracer width at the tee and at the landing
const GLOW_WIDTH = [10, 2.5];
const BALL_RADIUS = [9, 2.5]; // shrinks with distance, like the tracer

/** @param {number} n */
const offset = (n) => Math.sign(n) * OFFSET[Math.abs(n)];

/** Apex of a node in viewBox units. @param {{x: number, y: number}} node */
const apexOf = (node) => ({ x: CX + offset(node.x), y: CY - offset(node.y) });

/** Ball distance downrange at time u (0…1), slowing as drag takes over. @param {number} u */
const depth = (u) => CAMERA_Z + (CARRY_Z - CAMERA_Z) * (1 - (1 - u) ** 1.4);

/** Screen y of the ground directly under the ball. @param {number} u */
const groundY = (u) => HORIZON_Y + GROUND_K / depth(u);

/** @param {number} u */
const groundSlope = (u) => (groundY(u + 1e-4) - groundY(u - 1e-4)) / 2e-4;

/** Cubic Hermite between p0 and p1 with slopes m0/m1 over an interval of length h. */
function hermite(s, p0, m0, p1, m1, h) {
  const s2 = s * s;
  const s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * p0 + (s3 - 2 * s2 + s) * h * m0 + (3 * s2 - 2 * s3) * p1 + (s3 - s2) * h * m1;
}

/**
 * Flight through an apex. The ball always sits straight above its shadow; its
 * height above the shadow (lift) is shaped so the apex is exactly the dot and is
 * the highest point on screen; it keeps drifting outward as it falls.
 * @param {{x: number, y: number}} apex
 * @returns {(u: number) => {x: number, y: number, ground: number, z: number}}
 */
function flightPath(apex) {
  const dx = apex.x - CX;
  const drift = Math.sign(dx) * DRIFT_MAX * Math.tanh((DRIFT * Math.abs(dx)) / DRIFT_MAX);
  const landX = CX + dx + drift + DRAW_BIAS * Math.max(0, 1 - Math.abs(dx) / DRAW_FADE);
  const lift = groundY(APEX_T) - apex.y;
  const liftSlope = groundSlope(APEX_T);
  const fall = 1 - APEX_T;
  const xSlope = (1.2 * (landX - apex.x)) / fall;

  return (u) => {
    let x;
    let l;
    if (u <= APEX_T) {
      const s = u / APEX_T;
      x = hermite(s, CX, (0.35 * dx) / APEX_T, apex.x, xSlope, APEX_T);
      l = hermite(s, 0, (2.6 * lift) / APEX_T, lift, liftSlope, APEX_T);
    } else {
      const s = (u - APEX_T) / fall;
      x = hermite(s, apex.x, xSlope, landX, xSlope, fall);
      l = hermite(s, lift, liftSlope, 0, (-1.6 * lift) / fall, fall);
    }
    const ground = groundY(u);
    return { x, y: ground - l, ground, z: depth(u) };
  };
}

/**
 * SVG path data for a flight from 0 to `upTo`, drawn as a filled ribbon that
 * narrows from `startWidth` at the tee to `endWidth` at the landing, for perspective.
 * @param {(u: number) => {x: number, y: number, z: number}} path
 * @param {number} upTo
 * @param {[number, number]} widths
 */
function ribbonData(path, upTo, [startWidth, endWidth]) {
  const steps = 90;
  const points = [];
  for (let i = 0; i <= steps; i++) points.push(path((i / steps) * upTo));

  const left = [];
  const right = [];
  for (let i = 0; i <= steps; i++) {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(steps, i + 1)];
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const length = Math.hypot(tx, ty) || 1;
    const away = (points[i].z - CAMERA_Z) / (CARRY_Z - CAMERA_Z);
    const half = (startWidth + (endWidth - startWidth) * away) / 2;
    const nx = (-ty / length) * half;
    const ny = (tx / length) * half;
    left.push(`${(points[i].x + nx).toFixed(1)} ${(points[i].y + ny).toFixed(1)}`);
    right.push(`${(points[i].x - nx).toFixed(1)} ${(points[i].y - ny).toFixed(1)}`);
  }
  return `M${left.join('L')}L${right.reverse().join('L')}Z`;
}

/* ============================================================
   COMPONENT
============================================================ */
class FfFitter extends HTMLElement {
  /** @type {'right'|'left'} */
  #hand = 'right';
  /** @type {FitNode|null} */
  #miss = null;
  /** Setting recommended for the miss; keeps its dashed ring while the golfer browses. */
  #recommended = '';
  /** @type {any} */
  #flightTl = null;
  /** @type {any} */
  #resultsTl = null;
  #strings = {};
  #gsap = null;
  #reducedMotion = false;
  #fitter = false;

  connectedCallback() {
    this.#gsap = window.gsap ?? null;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#strings = JSON.parse(this.querySelector('[data-ff-strings]')?.textContent || '{}');

    this.#buildFlightNodes();
    this.#buildGrid();
    this.#bindEvents();
    this.#restoreFromUrl();
  }

  disconnectedCallback() {
    this.#flightTl?.kill();
    this.#resultsTl?.kill();
  }

  /* ---------- helpers ---------- */

  /** @param {string} selector */
  #q(selector) {
    return this.querySelector(selector);
  }

  get #animate() {
    return Boolean(this.#gsap) && !this.#reducedMotion;
  }

  /** @param {'landing'|'flight'|'results'} name */
  #showStage(name) {
    for (const stage of this.querySelectorAll('[data-ff-stage]')) {
      stage.hidden = stage.dataset.ffStage !== name;
    }
    window.scrollTo(0, this.getBoundingClientRect().top + window.scrollY);
  }

  /* ---------- stage 1: landing ---------- */

  #bindEvents() {
    const handSwitch = this.#q('[data-ff-hand-switch]');
    handSwitch.addEventListener('click', () => {
      this.#setHand(this.#hand === 'right' ? 'left' : 'right');
    });

    this.#q('[data-ff-next]').addEventListener('click', () => {
      this.#showStage('flight');
      this.#introFlight();
    });

    this.#q('[data-ff-submit]').addEventListener('click', () => this.#submit());
    this.#q('[data-ff-reset]').addEventListener('click', () => this.#reset());
    this.#q('[data-ff-clear]').addEventListener('click', () => this.#clearSelection());

    for (const button of this.querySelectorAll('[data-ff-hand]')) {
      button.addEventListener('click', () => this.#setHand(/** @type {'right'|'left'} */ (button.dataset.ffHand)));
    }
  }

  /** @param {'right'|'left'} hand */
  #setHand(hand) {
    this.#hand = hand;
    const handSwitch = this.#q('[data-ff-hand-switch]');
    handSwitch.setAttribute('aria-checked', String(hand === 'right'));

    const selected = this.#q('[data-ff-grid] .is-active')?.dataset.ffId;
    this.#buildGrid();

    const { upright, flat } = this.#strings;
    const outer = hand === 'left' ? flat : upright;
    const inner = hand === 'left' ? upright : flat;
    this.#q('[data-ff-label="tl"]').textContent = outer;
    this.#q('[data-ff-label="bl"]').textContent = outer;
    this.#q('[data-ff-label="tr"]').textContent = inner;
    this.#q('[data-ff-label="br"]').textContent = inner;
    const handLogo = this.#q('[data-ff-hand-logo]');
    if (handLogo instanceof HTMLImageElement) {
      handLogo.src = hand === 'left' ? this.dataset.logoLeft : this.dataset.logoRight;
    } else {
      handLogo.textContent = hand === 'left' ? this.#strings.handLeft : this.#strings.handRight;
    }

    for (const button of this.querySelectorAll('[data-ff-hand]')) {
      button.setAttribute('aria-pressed', String(button.dataset.ffHand === hand));
    }

    if (selected) this.#selectSetting(selected, { animate: false });
  }

  /* ---------- stage 2: ball flight ---------- */

  /** @param {FitNode} node */
  #flightLabel(node) {
    const s = this.#strings;
    if (node.x === 0 && node.y === 0) return s.flightStraight;
    const height = node.y > 0 ? s.flightHigh : node.y < 0 ? s.flightLow : '';
    const direction = node.x > 0 ? s.flightRight : node.x < 0 ? s.flightLeft : s.flightStraight.toLowerCase();
    return height ? `${height}, ${direction}` : direction.charAt(0).toUpperCase() + direction.slice(1);
  }

  #buildFlightNodes() {
    const container = this.#q('[data-ff-flight-nodes]');
    const fragment = document.createDocumentFragment();

    for (const node of NODES) {
      const apex = apexOf(node);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ff-flight__node';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.setAttribute('aria-label', this.#flightLabel(node));
      button.tabIndex = node.x === 0 && node.y === 0 ? 0 : -1;
      button.dataset.x = String(node.x);
      button.dataset.y = String(node.y);
      button.style.setProperty('--ff-node-x', `${(apex.x / 600) * 100}%`);
      button.style.setProperty('--ff-node-y', `${(apex.y / 900) * 100}%`);
      fragment.append(button);
    }
    container.append(fragment);

    container.addEventListener('click', (event) => {
      const button = event.target.closest('.ff-flight__node');
      if (button) this.#pickFlight(button);
    });
    container.addEventListener('keydown', (event) => this.#flightKeys(event));
  }

  /** @param {HTMLElement} button */
  #nodeFor(button) {
    return NODES.find((n) => n.x === Number(button.dataset.x) && n.y === Number(button.dataset.y));
  }

  /** Arrow keys move to the nearest dot in that direction. @param {KeyboardEvent} event */
  #flightKeys(event) {
    const dirs = { ArrowUp: [0, 1], ArrowDown: [0, -1], ArrowRight: [1, 0], ArrowLeft: [-1, 0] };
    const dir = dirs[event.key];
    const current = event.target.closest('.ff-flight__node');
    if (!dir || !current) return;
    event.preventDefault();

    const from = apexOf(this.#nodeFor(current));
    let best = null;
    let bestScore = Infinity;
    for (const button of this.querySelectorAll('.ff-flight__node')) {
      const to = apexOf(this.#nodeFor(button));
      const dx = to.x - from.x;
      const dy = from.y - to.y;
      const along = dx * dir[0] + dy * dir[1];
      if (along <= 0) continue;
      const across = Math.abs(dx * dir[1]) + Math.abs(dy * dir[0]);
      const score = along + across * 3;
      if (score < bestScore) {
        bestScore = score;
        best = button;
      }
    }
    if (best) {
      best.focus();
      this.#pickFlight(best);
    }
  }

  /** Dots bloom out from the centre the first time the chart shows. */
  #introFlight() {
    if (!this.#animate || this.dataset.flightIntro) return;
    this.dataset.flightIntro = 'done';
    const buttons = [...this.querySelectorAll('.ff-flight__node')];
    this.#gsap.from(buttons, {
      scale: 0,
      opacity: 0,
      duration: 0.45,
      ease: 'back.out(2)',
      delay: (i) => Math.hypot(Number(buttons[i].dataset.x), Number(buttons[i].dataset.y)) * 0.05,
      clearProps: 'opacity,scale,transform',
    });
    this.#gsap.from(this.#q('.ff-flight__flower'), { opacity: 0, duration: 0.8, clearProps: 'opacity' });
  }

  /** @param {HTMLElement} button */
  #pickFlight(button) {
    const node = this.#nodeFor(button);
    if (!node || node === this.#miss) return;
    this.#miss = node;

    for (const other of this.querySelectorAll('.ff-flight__node')) {
      const checked = other === button;
      other.setAttribute('aria-checked', String(checked));
      other.tabIndex = checked ? 0 : -1;
    }
    this.#q('.ff-flight').classList.add('has-selection');
    const submit = this.#q('[data-ff-submit]');
    if (submit.hidden) {
      submit.hidden = false;
      if (this.#animate) this.#gsap.from(submit, { opacity: 0, y: 10, duration: 0.4, clearProps: 'opacity,transform' });
    }

    this.#flyBall(apexOf(node));
  }

  /**
   * Tracer: ball climbs to the apex and drops toward the fairway, trail drawn behind
   * it and its shadow racing along the ground. The previous trail fades out.
   * @param {{x: number, y: number}} apex
   */
  #flyBall(apex) {
    const trail = this.#q('[data-ff-trail]');
    const glow = this.#q('[data-ff-glow]');
    const oldTrail = this.#q('[data-ff-trail-old]');
    const path = flightPath(apex);

    this.#flightTl?.kill();
    oldTrail.setAttribute('d', trail.getAttribute('d') || '');

    if (!this.#animate) {
      this.#drawFlight(path, 1);
      return;
    }

    const progress = { u: 0 };
    this.#flightTl = this.#gsap
      .timeline()
      .fromTo(oldTrail, { opacity: 0.6 }, { opacity: 0, duration: 0.35 }, 0)
      .set([trail, glow], { opacity: 1 }, 0)
      .to(progress, { u: 1, duration: 1.5, ease: 'none', onUpdate: () => this.#drawFlight(path, progress.u) }, 0);
  }

  /**
   * Render a flight up to progress u: trail, ball and shadow.
   * @param {(u: number) => {x: number, y: number, ground: number, z: number}} path
   * @param {number} u
   */
  #drawFlight(path, u) {
    this.#q('[data-ff-trail]').setAttribute('d', ribbonData(path, u, TRAIL_WIDTH));
    this.#q('[data-ff-glow]').setAttribute('d', ribbonData(path, u, GLOW_WIDTH));

    const p = path(u);
    const away = (p.z - CAMERA_Z) / (CARRY_Z - CAMERA_Z); // 0 at the tee, 1 at the landing
    const radius = BALL_RADIUS[0] + (BALL_RADIUS[1] - BALL_RADIUS[0]) * away;
    const ball = this.#q('[data-ff-ball]');
    ball.setAttribute('cx', p.x.toFixed(1));
    ball.setAttribute('cy', p.y.toFixed(1));
    ball.setAttribute('r', radius.toFixed(2));

    const shadow = this.#q('[data-ff-shadow]');
    shadow.setAttribute('cx', p.x.toFixed(1));
    shadow.setAttribute('cy', p.ground.toFixed(1));
    shadow.setAttribute('rx', (radius * 0.8).toFixed(2));
    shadow.setAttribute('ry', (radius * 0.22).toFixed(2));
  }

  #submit() {
    const miss = this.#miss;
    if (!miss) return;
    this.#flightTl?.progress(1).kill();
    this.#showStage('results');
    this.#showRecommendation(miss, { animate: this.#animate });
    this.#writeUrl(miss);
    this.#sendWebhook(miss);
  }

  /* ---------- stage 3: results ---------- */

  #buildGrid() {
    const container = this.#q('[data-ff-grid]');
    const lieScale = this.#hand === 'left' ? [...FF_SCALE].reverse() : FF_SCALE;
    const byCoord = new Map(NODES.map((n) => [`${n.x},${n.y}`, n]));
    const fragment = document.createDocumentFragment();

    for (let row = 7; row > -8; row--) {
      for (let col = -7; col < 8; col++) {
        const item = document.createElement('div');
        item.className = 'grid-item';
        item.dataset.x = String(col);
        item.dataset.y = String(row);
        item.dataset.loft = FF_SCALE[7 - row];
        item.dataset.lie = lieScale[col + 7];

        const node = byCoord.get(`${col},${row}`);
        if (node) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `grid-item-content ${node.tier}-node`;
          button.dataset.ffId = node.id;
          button.classList.toggle('is-recommended', node.id === this.#recommended);
          button.setAttribute('aria-pressed', 'false');
          button.textContent = node.id.toUpperCase();
          item.append(button);
        }
        fragment.append(item);
      }
    }
    container.replaceChildren(fragment);

    if (!container.dataset.bound) {
      container.dataset.bound = 'true';
      container.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ff-id]');
        if (!button) return;
        this.#resultsTl?.progress(1);
        this.#selectSetting(button.dataset.ffId, { animate: true });
      });
    }
  }

  /** @param {string} id */
  #gridButton(id) {
    return this.#q(`[data-ff-grid] [data-ff-id="${id}"]`);
  }

  /**
   * Highlight a setting and fill in the panel.
   * @param {string} id
   * @param {{animate: boolean}} options
   */
  #selectSetting(id, { animate }) {
    const node = NODES.find((n) => n.id === id);
    const button = this.#gridButton(id);
    if (!node || !button) return;

    const item = button.parentElement;
    for (const other of this.querySelectorAll('[data-ff-grid] [data-ff-id]')) {
      const isActive = other === button;
      const isRec = node.recs.includes(other.dataset.ffId);
      other.classList.toggle('is-active', isActive);
      other.classList.toggle('is-rec', isRec);
      other.classList.toggle('is-dim', !isActive && !isRec);
      other.setAttribute('aria-pressed', String(isActive));
    }

    this.#q('[data-ff-out="setting"]').textContent = id.toUpperCase();
    this.#setHeading(id);
    this.#q('[data-ff-out="recs"]').textContent = node.recs.length ? node.recs.join(', ').toUpperCase() : '--';

    const s = this.#strings;
    this.#countUp('loft', item.dataset.loft, s.higher, s.lower, animate);
    this.#countUp('lie', item.dataset.lie, s.uprightWord, s.flatWord, animate);

    if (animate && this.#animate) {
      this.#gsap.fromTo(button, { scale: 0.6 }, { scale: 1.35, duration: 0.5, ease: 'back.out(3)', clearProps: 'scale' });
    }
  }

  /**
   * "+1.4° higher", counting up from zero.
   * @param {'loft'|'lie'} key
   * @param {string} value e.g. "+1.4", "-0.7", "STD"
   * @param {string} plusWord
   * @param {string} minusWord
   * @param {boolean} animate
   */
  #countUp(key, value, plusWord, minusWord, animate) {
    const out = this.#q(`[data-ff-out="${key}"]`);
    const amount = Number.parseFloat(value);
    if (!value || Number.isNaN(amount)) {
      out.textContent = value || '--';
      return;
    }
    const sign = amount > 0 ? '+' : '-';
    const word = amount > 0 ? plusWord : minusWord;
    const format = (v) => `${sign}${Math.abs(v).toFixed(1).replace(/\.0$/, '')}° ${word}`;

    if (!animate || !this.#animate) {
      out.textContent = format(amount);
      return;
    }
    const counter = { v: 0 };
    this.#gsap.to(counter, {
      v: amount,
      duration: 0.6,
      ease: 'power2.out',
      onUpdate: () => {
        out.textContent = format(counter.v);
      },
      onComplete: () => {
        out.textContent = format(amount);
      },
    });
  }

  /**
   * Results reveal: settings bloom in from A1, then the mirrored setting is selected.
   * @param {FitNode} miss
   * @param {{animate: boolean}} options
   */
  #showRecommendation(miss, { animate }) {
    const rec = NODES.find((n) => n.x === -miss.x && n.y === -miss.y);
    if (!rec) return;
    this.#recommended = rec.id;
    for (const button of this.querySelectorAll('[data-ff-grid] [data-ff-id]')) {
      button.classList.toggle('is-recommended', button.dataset.ffId === rec.id);
    }

    if (!animate) {
      this.#selectSetting(rec.id, { animate: false });
      return;
    }

    const buttons = [...this.querySelectorAll('[data-ff-grid] [data-ff-id]')];
    const distance = (el) => Math.hypot(Number(el.parentElement.dataset.x), Number(el.parentElement.dataset.y));

    this.#resultsTl?.kill();
    this.#resultsTl = this.#gsap
      .timeline()
      .from(
        buttons,
        {
          opacity: 0,
          scale: 0.4,
          duration: 0.4,
          ease: 'back.out(2)',
          delay: (i) => distance(buttons[i]) * 0.04,
          clearProps: 'opacity,scale',
        },
        0,
      )
      .call(() => this.#selectSetting(rec.id, { animate: true }), [], 0.7);
  }

  #clearSelection() {
    this.#resultsTl?.progress(1);
    for (const button of this.querySelectorAll('[data-ff-grid] [data-ff-id]')) {
      button.classList.remove('is-active', 'is-rec', 'is-dim');
      button.setAttribute('aria-pressed', 'false');
    }
    for (const key of ['setting', 'loft', 'lie', 'recs']) {
      this.#q(`[data-ff-out="${key}"]`).textContent = '--';
    }
    this.#setHeading('');
  }

  /** "Your Recommended Setting" only while the recommendation itself is selected. @param {string} id */
  #setHeading(id) {
    const s = this.#strings;
    const isRecommended = Boolean(this.#recommended) && id === this.#recommended;
    this.#q('[data-ff-rec-heading]').textContent = isRecommended ? s.recommendedHeading : s.settingHeading;
  }

  #reset() {
    this.#flightTl?.kill();
    this.#recommended = '';
    this.#q('[data-ff-grid] .is-recommended')?.classList.remove('is-recommended');
    this.#clearSelection();
    this.#miss = null;
    const url = new URL(window.location.href);
    url.searchParams.delete('hand');
    url.searchParams.delete('miss');
    window.history.replaceState(null, '', url);

    for (const button of this.querySelectorAll('.ff-flight__node')) {
      button.setAttribute('aria-checked', 'false');
      button.tabIndex = button.dataset.x === '0' && button.dataset.y === '0' ? 0 : -1;
    }
    for (const selector of ['[data-ff-trail]', '[data-ff-glow]', '[data-ff-trail-old]']) {
      this.#q(selector).setAttribute('d', '');
    }
    this.#q('[data-ff-ball]').setAttribute('r', '0');
    this.#q('[data-ff-shadow]').setAttribute('rx', '0');
    this.#q('.ff-flight').classList.remove('has-selection');
    this.#q('[data-ff-submit]').hidden = true;
    this.#showStage('landing');
  }

  /* ---------- URL state, fitter mode, webhook ---------- */

  /** @param {FitNode} miss */
  #writeUrl(miss) {
    const url = new URL(window.location.href);
    url.searchParams.set('hand', this.#hand);
    url.searchParams.set('miss', `${miss.x},${miss.y}`);
    window.history.replaceState(null, '', url);
  }

  #restoreFromUrl() {
    const params = new URL(window.location.href).searchParams;

    if (params.get('fitter')) {
      this.#fitter = true;
      this.#q('[data-ff-reset]').hidden = true;
      this.#setHeading('');
      for (const el of this.querySelectorAll('[data-ff-fitter-only]')) el.hidden = false;
      this.#setHand('right');
      this.#showStage('results');
      return;
    }

    const hand = params.get('hand');
    if (hand === 'left' || hand === 'right') this.#setHand(hand);

    const [x, y] = (params.get('miss') || '').split(',').map(Number);
    const miss = NODES.find((n) => n.x === x && n.y === y);
    if (!miss) return;
    this.#miss = miss;
    this.#showStage('results');
    this.#showRecommendation(miss, { animate: false });
  }

  /** @param {FitNode} miss */
  async #sendWebhook(miss) {
    const endpoint = this.dataset.webhook;
    if (!endpoint || this.#fitter) return;
    const rec = NODES.find((n) => n.x === -miss.x && n.y === -miss.y);
    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hand: this.#hand, miss_x: miss.x, miss_y: miss.y, recommended_setting: rec ? rec.id : '' }),
      });
    } catch {
      // Reporting only; never block the golfer.
    }
  }
}

if (!customElements.get('ff-fitter')) customElements.define('ff-fitter', FfFitter);
